import { useState, type FC } from 'react';
import Typography, { TypographyProps } from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import MaterialDeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from 'react-i18next';

import { IconButton, Modal, Button } from '@bloomays-lib/ui.shared';
import { TalentBoardComment as TalentBoardCommentType } from '@bloomays-lib/types.shared';

import useDeleteTalentBoardComment from '../../hooks/useDeleteTalentBoardComment';
import { useTalentsForTalentBoardStore } from '../../stores/talentsForTalentBoardStore';
import { useTalentBoardStore } from '../../stores/talentBoardStore';

type DeleteCommentButtonProps = {
  comment: TalentBoardCommentType;
};

export const DeleteCommentButton = ({ comment }: DeleteCommentButtonProps) => {
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const { t } = useTranslation(['talentBoard', 'random']);
  const { deleteTalentBoardComment } = useDeleteTalentBoardComment();
  const { deleteTalentComments } = useTalentsForTalentBoardStore();
  const { deleteSelectedTalentComments } = useTalentBoardStore();

  const handleDeleteClick = () => {
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    const success = await deleteTalentBoardComment({ id: comment.id });
    if (success) {
      deleteTalentComments(comment.jobId, comment.talentId, comment.id);
      deleteSelectedTalentComments(comment.id);
    }
    setIsDeleteModalOpen(false);
  };

  const handleDeleteCancel = () => {
    setIsDeleteModalOpen(false);
  };

  return (
    <>
      <IconButtonDelete onClick={handleDeleteClick} variant="contained" disableRipple>
        <DeleteIcon />
        <DeleteText>{t('delete', { ns: 'random' })}</DeleteText>
      </IconButtonDelete>

      <Modal
        open={isDeleteModalOpen}
        onClose={handleDeleteCancel}
        additionnalCSS={{
          width: '400px',
          padding: '24px',
          borderRadius: '8px',
        }}
      >
        <ConfirmModalContent>
          <ConfirmTitle>{t('talentJob.deleteCommentConfirm', { ns: 'talentBoard' })}</ConfirmTitle>
          <ConfirmButtons>
            <Button
              textButton={t('no', { ns: 'random' })}
              buttonType="button"
              color="secondary"
              onClick={handleDeleteCancel}
            />
            <Button textButton={t('yes', { ns: 'random' })} buttonType="button" onClick={handleDeleteConfirm} />
          </ConfirmButtons>
        </ConfirmModalContent>
      </Modal>
    </>
  );
};

const DeleteIcon = styled(MaterialDeleteIcon)(({ theme }) => ({
  color: theme.palette.neutral.black,
  width: '12px',
  height: '12px',
}));

const DeleteText: FC<TypographyProps> = styled(Typography)(({ theme }) => ({
  fontSize: '10px',
  color: theme.palette.neutral.black,
  textDecoration: 'underline',
  cursor: 'pointer',
}));

const IconButtonDelete = styled(IconButton)(() => ({
  padding: '0',
  display: 'flex',
  gap: '4px',
}));

const ConfirmModalContent = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
}));

const ConfirmTitle = styled(Typography)(() => ({
  fontSize: '16px',
  fontFamily: 'Poppins Medium',
}));

const ConfirmButtons = styled('div')(() => ({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '8px',
}));

export default DeleteCommentButton;
